import { useCallback, useEffect, useRef } from 'react'
import { api } from './api'

const HEARTBEAT_MS = 20_000
const SECTION_PATTERN = /^[a-z0-9_-]{1,80}$/

interface SessionState {
  id: string
  section: string
  activeSince: number
  visible: boolean
  ready: Promise<unknown>
  ended: boolean
}

function initialSection(): string {
  const hash = window.location.hash.replace(/^#/, '')
  return SECTION_PATTERN.test(hash) ? hash : 'overview'
}

export function useWorkspaceSession(publicId: string, enabled: boolean) {
  const stateRef = useRef<SessionState | null>(null)

  const send = useCallback((payload: Record<string, unknown>, beacon = false) => {
    const state = stateRef.current
    if (!state) return
    const body = { ...payload, sessionId: state.id }
    if (beacon && navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(body)], { type: 'application/json' })
      navigator.sendBeacon(`/api/workspaces/${encodeURIComponent(publicId)}/session`, blob)
      return
    }
    void state.ready.then(() => api.recordSession(publicId, body as Parameters<typeof api.recordSession>[1])).catch(() => undefined)
  }, [publicId])

  const flush = useCallback((action: 'heartbeat' | 'end' = 'heartbeat', beacon = false) => {
    const state = stateRef.current
    if (!state || state.ended) return
    const now = Date.now()
    const durationMs = state.visible ? Math.max(0, Math.min(300_000, now - state.activeSince)) : 0
    state.activeSince = now
    if (action === 'end') state.ended = true
    send({ action, sectionKey: state.section, durationMs }, beacon)
  }, [send])

  useEffect(() => {
    if (!enabled) return
    const id = crypto.randomUUID()
    const section = initialSection()
    const ready = api.recordSession(publicId, { action: 'start', sessionId: id, sectionKey: section }).catch(() => undefined)
    stateRef.current = { id, section, activeSince: Date.now(), visible: document.visibilityState === 'visible', ready, ended: false }

    const interval = window.setInterval(() => flush('heartbeat'), HEARTBEAT_MS)
    const onVisibility = () => {
      const state = stateRef.current
      if (!state || state.ended) return
      if (document.visibilityState === 'hidden') {
        flush('heartbeat', true)
        state.visible = false
      } else {
        state.visible = true
        state.activeSince = Date.now()
      }
    }
    const onPageHide = () => flush('end', true)
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('pagehide', onPageHide)
    return () => {
      window.clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('pagehide', onPageHide)
      flush('end', true)
      stateRef.current = null
    }
  }, [enabled, flush, publicId])

  return useCallback((eventType: string, targetId?: string) => {
    const state = stateRef.current
    if (!state || state.ended) return
    if (eventType === 'tab_view' && targetId && SECTION_PATTERN.test(targetId)) {
      flush('heartbeat')
      state.section = targetId
      state.activeSince = Date.now()
      return
    }
    if ((eventType === 'asset_open' || eventType === 'asset_download') && targetId) {
      send({ action: 'heartbeat', click: { type: eventType, targetId } })
    }
  }, [flush, send])
}
